/**
 * @file: useNotificationSettings.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import {
  NotificationSettingCategory,
  NotificationSettingChannel,
  NotificationSettings,
  defaultNotificationSettings,
  notificationSettingCategories,
  notificationSettingChannels,
} from '@euler/model';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import produce from 'immer';
import { useEffect, useState } from 'react';
import Toast from 'react-native-root-toast';

// Overlay a (possibly partial) server response onto the all-enabled default so
// every channel × category cell is always present — the UI never reads an
// undefined cell even if the backend omits one.
const mergeSettings = (
  res: Partial<NotificationSettings> | null | undefined,
): NotificationSettings =>
  produce(defaultNotificationSettings(), draft => {
    for (const channel of notificationSettingChannels) {
      for (const category of notificationSettingCategories) {
        const value = res?.[channel]?.[category];
        if (value != null) {
          draft[channel][category] = value;
        }
      }
    }
  });

export const useNotificationSettings = () => {
  const [settings, setSettings] = useState<NotificationSettings>(
    defaultNotificationSettings(),
  );
  const { userService } = useServiceFactory();

  // Lazy: callers invoke `reload` when the surface opens (the modal does on
  // show), which also resyncs to server truth after a failed toggle.
  const reload = usePersistFn(async () => {
    try {
      const res = await userService.getNotificationSettings();
      setSettings(mergeSettings(res));
    } catch (e) {
      onErrorIgnore(e);
    }
  });

  // Read from the server whenever the settings surface mounts (each open of
  // the dedicated page / inbox screen).
  useEffect(() => {
    void reload();
  }, [reload]);

  const updateAsync = usePersistFn(
    async (
      channel: NotificationSettingChannel,
      category: NotificationSettingCategory,
      enabled: boolean,
    ) => {
      // Optimistic flip; capture the exact prior value to restore on failure.
      let prevValue = enabled;
      setSettings(prev => {
        prevValue = prev[channel][category];
        return produce(prev, draft => {
          draft[channel][category] = enabled;
        });
      });
      try {
        await userService.updateNotificationSetting(channel, category, enabled);
      } catch (e) {
        // Revert only if our optimistic value is still showing — a later
        // toggle on the same cell wins and must not be clobbered.
        setSettings(prev =>
          prev[channel][category] === enabled
            ? produce(prev, draft => {
                draft[channel][category] = prevValue;
              })
            : prev,
        );
        Toast.show('设置失败，请重试', {
          duration: Toast.durations.SHORT,
          position: Toast.positions.BOTTOM,
        });
        onErrorIgnore(e);
      }
    },
  );

  return { settings, updateAsync, reload };
};
